import React from 'react';
import {
    X, MapPin, Bookmark, Users, User as UserIcon,
    BarChart3, Globe, Image, Settings as SettingsIcon, LogOut, Sparkles
} from 'lucide-react';
import { AppView } from '../types';

import { isAndroid } from '../utils/platformUtils';

interface MoreMenuProps {
    isOpen: boolean;
    onClose: () => void;
    onNavigate: (view: AppView) => void;
    onLogout: () => void;
    currentView: AppView;
}

export const MoreMenu: React.FC<MoreMenuProps> = ({ isOpen, onClose, onNavigate, onLogout, currentView }) => {
    if (!isOpen) return null;
    const communityEnabled = !isAndroid();

    const menuItems = [
        { view: AppView.YOUR_NEXT_STICK, icon: Sparkles, label: 'Next Stick' },
        { view: AppView.LOCATOR, icon: MapPin, label: 'Locate' },
        { view: AppView.WISHLIST, icon: Bookmark, label: 'Wishlist' },
        { view: AppView.ANALYTICS, icon: BarChart3, label: 'Analytics' },
        { view: AppView.JOURNEY, icon: Globe, label: 'Journey' },
        { view: AppView.GALLERY, icon: Image, label: 'Gallery' },
        ...(communityEnabled ? [{ view: AppView.COMMUNITY, icon: Users, label: 'Community' }] : []),
        { view: AppView.PROFILE, icon: UserIcon, label: 'Profile' },
        { view: AppView.SETTINGS, icon: SettingsIcon, label: 'Settings' },
    ];

    return (
        <div className="fixed inset-0 z-[60] flex flex-col justify-end md:hidden">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in"
                onClick={onClose}
            />

            {/* Menu Content */}
            <div className="relative bg-brand-900 border-t border-brand-700 rounded-t-3xl p-6 shadow-2xl animate-slide-up">
                {/* Handle bar for visual cue */}
                <div className="w-12 h-1.5 bg-brand-700 rounded-full mx-auto mb-6" />

                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-serif text-gold-500 font-bold">Menu</h2>
                    <button onClick={onClose} className="p-2 text-brand-400 hover:text-brand-200">
                        <X size={24} />
                    </button>
                </div>

                <div className="grid grid-cols-4 gap-4 mb-8">
                    {menuItems.map((item, index) => (
                        <button
                            key={item.label}
                            onClick={() => {
                                onNavigate(item.view);
                                onClose();
                            }}
                            className={`flex flex-col items-center gap-2 p-3 rounded-xl transition-all ${currentView === item.view
                                ? 'bg-brand-800 text-gold-500 ring-1 ring-gold-500/30'
                                : 'bg-brand-950/50 text-brand-100 hover:bg-brand-800 hover:text-white'
                                }`}
                            style={{ animationDelay: `${index * 50}ms` }}
                        >
                            <div className={`p-3 rounded-full ${currentView === item.view ? 'bg-gold-500/10' : 'bg-brand-900'}`}>
                                <item.icon size={24} />
                            </div>
                            <span className="text-[10px] font-medium tracking-wide">{item.label}</span>
                        </button>
                    ))}
                </div>

                <button
                    onClick={onLogout}
                    className="w-full flex items-center justify-center gap-2 p-4 rounded-xl text-white bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 border border-red-500/40 shadow-lg shadow-red-900/30 transition-all active:scale-[0.98]"
                >
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/10 border border-white/20">
                        <LogOut size={18} />
                    </span>
                    <span className="font-semibold tracking-wide">Sign Out</span>
                </button>

                <div className="h-8" /> {/* Safe area spacer */}
            </div>
        </div>
    );
};
